/* 知識圖譜：canvas 繪製 ＋ 可縮放平移 ＋ 標籤 LOD ＋ 框選匯出。
   排版是純函式（src/graph/layout.ts），這裡只負責畫與操作。 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Category, Concept, KnowledgeLink } from "../domain/types.ts";
import { deriveTitle, linkCountFor, checkNewLink } from "../domain/rules.ts";
import type { RuleCode } from "../domain/rules.ts";
import { layoutGraph, labelsVisibleAt, hitTest } from "../graph/layout.ts";
import type { GraphLayout } from "../graph/layout.ts";
import { useI18n } from "../i18n/index.tsx";
import { downloadBlob } from "./download.ts";

const MOBILE_MAX_NODES = 800;
const DESKTOP_MAX_NODES = 2000;

export function GraphPanel(props: {
  concepts: Concept[]; links: KnowledgeLink[]; categories: Category[];
  onExported: (nodeCount: number) => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 360, height: 420 });
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [selected, setSelected] = useState<string[]>([]);
  const drag = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: Math.max(320, el.clientHeight) });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const maxNodes = typeof window !== "undefined" && window.innerWidth < 1024
    ? MOBILE_MAX_NODES : DESKTOP_MAX_NODES;

  const layout: GraphLayout = useMemo(() => layoutGraph({
    concepts: props.concepts.map((c) => ({ id: c.id, summary: c.summary, primaryCategoryId: c.primaryCategoryId })),
    links: props.links.map((l) => ({ fromId: l.fromId, toId: l.toId, type: l.type })),
    categories: props.categories.map((c) => ({ id: c.id, name: c.name, seedKey: c.seedKey })),
    width: size.width, height: size.height, maxNodes,
  }), [props.concepts, props.links, props.categories, size, maxNodes]);

  const draw = useCallback(() => {
    const cv = canvas.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = size.width * dpr;
    cv.height = size.height * dpr;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const css = getComputedStyle(document.body);
    const paper = css.getPropertyValue("--sheet").trim() || "#1E241F";
    const ink = css.getPropertyValue("--sheet-ink").trim() || "#E6E1D3";
    const dim = css.getPropertyValue("--sheet-dim").trim() || "#A8B5A6";
    const plant = css.getPropertyValue("--plant").trim() || "#6B7D63";
    const label = css.getPropertyValue("--label").trim() || "#D9A441";

    ctx.fillStyle = css.getPropertyValue("--ground-deep").trim() || "#1C221E";
    ctx.fillRect(0, 0, size.width, size.height);
    ctx.save();
    ctx.translate(view.x, view.y);
    ctx.scale(view.scale, view.scale);

    /* 棉線 */
    ctx.strokeStyle = plant;
    ctx.lineWidth = 1 / view.scale;
    for (const e of layout.edges) {
      const a = layout.nodes.find((n) => n.id === e.from);
      const b = layout.nodes.find((n) => n.id === e.to);
      if (!a || !b) continue;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }

    /* 吊牌（關係類型）：只在標籤可見時畫，而且只有選中的節點才畫 —— 否則會糊成一團 */
    if (labelsVisibleAt(view.scale)) {
      ctx.font = (10 / view.scale) + "px ui-monospace, Consolas, monospace";
      ctx.textAlign = "center";
      for (const e of layout.edges) {
        if (!selected.includes(e.from) && !selected.includes(e.to)) continue;
        const a = layout.nodes.find((n) => n.id === e.from);
        const b = layout.nodes.find((n) => n.id === e.to);
        if (!a || !b) continue;
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        const w = ctx.measureText(e.type).width + 8;
        ctx.fillStyle = label;
        ctx.fillRect(mx - w / 2, my - 7, w, 14);
        ctx.fillStyle = paper;
        ctx.fillText(e.type, mx, my + 3);
      }
    }

    /* 節點 */
    for (const n of layout.nodes) {
      const isSelected = selected.includes(n.id);
      ctx.fillStyle = n.kind === "group" ? plant : isSelected ? label : ink;
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.fill();
      if (isSelected) {
        ctx.strokeStyle = label;
        ctx.lineWidth = 2 / view.scale;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + 4, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    /* 標籤 */
    if (labelsVisibleAt(view.scale)) {
      ctx.fillStyle = dim;
      ctx.font = (11 / view.scale) + "px -apple-system, 'PingFang TC', 'Noto Sans TC', sans-serif";
      ctx.textAlign = "center";
      for (const n of layout.nodes) {
        ctx.fillText(n.kind === "group" ? n.label + " (" + n.count + ")" : n.label, n.x, n.y - n.r - 4);
      }
    }
    ctx.restore();
  }, [layout, size, view, selected]);

  useEffect(() => { draw(); }, [draw]);

  const toGraph = (clientX: number, clientY: number) => {
    const rect = canvas.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left - view.x) / view.scale, y: (clientY - rect.top - view.y) / view.scale };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const p = toGraph(e.clientX, e.clientY);
    const hit = hitTest(layout, p.x, p.y);
    if (hit) {
      const isGroup = hit.kind === "group";
      if (isGroup) {
        /* 聚合球：把該類別的全部知識都選起來（框選匯出的前置動作） */
        const ids = props.concepts.filter((c) => c.primaryCategoryId === hit.categoryId).map((c) => c.id);
        setSelected((s) => (s.length === ids.length ? [] : ids));
        return;
      }
      setSelected((s) => (s.includes(hit.id) ? s.filter((id) => id !== hit.id) : [...s, hit.id]));
      return;
    }
    drag.current = { x: e.clientX, y: e.clientY, vx: view.x, vy: view.y };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setView((v) => ({ ...v, x: drag.current!.vx + (e.clientX - drag.current!.x),
      y: drag.current!.vy + (e.clientY - drag.current!.y) }));
  };
  const onPointerUp = () => { drag.current = null; };
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setView((v) => {
      const next = Math.max(0.25, Math.min(3, v.scale * (e.deltaY < 0 ? 1.12 : 0.89)));
      return { ...v, scale: next };
    });
  };

  /** 匯出 PNG：永遠帶標籤（這是給人看的成品），選中的節點標黃。 */
  const exportPng = () => {
    const ids = selected.length ? selected : layout.nodes.map((n) => n.id);
    const out = document.createElement("canvas");
    const pad = 40;
    out.width = Math.round(size.width * 2 + pad * 2);
    out.height = Math.round(size.height * 2 + pad * 2 + 70);
    const ctx = out.getContext("2d");
    if (!ctx) return;
    ctx.scale(2, 2);
    ctx.fillStyle = getComputedStyle(document.body).getPropertyValue("--ground") || "#2A322C";
    ctx.fillRect(0, 0, out.width / 2, out.height / 2);
    ctx.fillStyle = getComputedStyle(document.body).getPropertyValue("--label") || "#D9A441";
    ctx.fillRect(0, 0, out.width / 2, 6);
    ctx.fillStyle = getComputedStyle(document.body).getPropertyValue("--ink") || "#E6E1D3";
    ctx.font = "600 18px -apple-system, 'PingFang TC', sans-serif";
    ctx.fillText(t("graph.title"), pad, pad + 8);
    ctx.font = "12px ui-monospace, Consolas, monospace";
    ctx.fillStyle = getComputedStyle(document.body).getPropertyValue("--dim") || "#A8B5A6";
    ctx.fillText(t("graph.nodes", { n: ids.length, m: layout.edges.length }), pad, pad + 30);

    const chosen = layout.nodes.filter((n) => ids.includes(n.id));
    const minX = Math.min(...chosen.map((n) => n.x)) - 20;
    const minY = Math.min(...chosen.map((n) => n.y)) - 20;
    ctx.save();
    ctx.translate(pad - minX, pad + 50 - minY);
    ctx.strokeStyle = getComputedStyle(document.body).getPropertyValue("--plant") || "#6B7D63";
    for (const e of layout.edges) {
      if (!ids.includes(e.from) || !ids.includes(e.to)) continue;
      const a = layout.nodes.find((n) => n.id === e.from)!;
      const b = layout.nodes.find((n) => n.id === e.to)!;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
    for (const n of chosen) {
      ctx.fillStyle = n.kind === "group"
        ? (getComputedStyle(document.body).getPropertyValue("--plant") || "#6B7D63")
        : (getComputedStyle(document.body).getPropertyValue("--ink") || "#E6E1D3");
      ctx.beginPath();
      ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = getComputedStyle(document.body).getPropertyValue("--dim") || "#A8B5A6";
      ctx.font = "11px -apple-system, 'PingFang TC', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(n.label, n.x, n.y - n.r - 4);
    }
    ctx.restore();
    ctx.textAlign = "left";
    out.toBlob((blob) => {
      if (blob) downloadBlob(blob, "知識圖譜-" + new Date().toISOString().slice(0, 10) + ".png");
    }, "image/png");
    return ids.length;
  };

  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("graph.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("graph.hint")}</p>
      <p className="hint mono" data-graph-stats>
        {t("graph.nodes", { n: layout.nodes.length, m: layout.edges.length })}
        {selected.length ? "　·　" + t("graph.selected", { n: selected.length }) : ""}
      </p>
      {layout.aggregated && (
        <p className="hint bad">{t("graph.aggregated", { hidden: layout.hidden })}</p>)}
      {layout.nodes.length === 0 && <p className="hint dim">{t("graph.empty")}</p>}
      <div className="canvasbox" ref={wrap} style={{ height: "46dvh" }}>
        <canvas ref={canvas} data-graph-canvas style={{ width: "100%", height: "100%", touchAction: "none" }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
          onWheel={onWheel} />
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <button className="btn" onClick={() => setView((v) => ({ ...v, scale: Math.min(3, v.scale * 1.2) }))}>
          {t("graph.zoomIn")}</button>
        <button className="btn" onClick={() => setView((v) => ({ ...v, scale: Math.max(0.25, v.scale / 1.2) }))}>
          {t("graph.zoomOut")}</button>
        <button className="btn" onClick={() => setView({ scale: 1, x: 0, y: 0 })}>{t("graph.reset")}</button>
        <button className="btn" onClick={() => setSelected([])}>{t("graph.clear")}</button>
        <button className="btn primary" data-graph-export onClick={() => {
          const n = exportPng();
          if (n) props.onExported(n);
        }}>{t("graph.export")}</button>
      </div>
      <p className="hint">{t("graph.pick")}</p>
    </section>
  );
}

/** 拉棉線：挑目標、選類型、寫吊牌。軟上限到達時要填理由。 */
export function LinkComposer(props: {
  current: Concept; concepts: Concept[]; links: KnowledgeLink[]; cap: number;
  onCreate: (toId: string, type: string, note: string) => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [toId, setToId] = useState("");
  const [type, setType] = useState("相關");
  const [note, setNote] = useState("");
  const check = toId ? checkNewLink(props.links, props.current.id, toId, props.cap) : null;
  const candidates = props.concepts
    .filter((c) => c.id !== props.current.id && c.status !== "trashed" && !c.conflictOf)
    .filter((c) => !query.trim() || (c.summary + " " + c.note + " " + c.title).toLowerCase().includes(query.toLowerCase()))
    .slice(0, 20);
  const codeText = (code: RuleCode | null) => {
    if (!code) return "";
    const map: Record<string, string> = {
      "link.error.self": t("link.self"), "link.error.duplicate": t("link.duplicate"),
      "link.warn.capReached": t("link.cap", { cap: props.cap, from: check?.params?.["from"] ?? 0,
        to: check?.params?.["to"] ?? 0 }),
    };
    return map[code] ?? "";
  };
  const ok = !!check?.allowed && (!check.requiresReason || note.trim().length >= 4);

  return (
    <div className="panel" data-link-composer>
      <h2 style={{ display: "block" }}>{t("link.new")}</h2>
      <input className="input" aria-label={t("link.target")} placeholder={t("link.target")}
        value={query} onChange={(e) => setQuery(e.target.value)} data-link-search />
      <div style={{ maxHeight: 180, overflowY: "auto", margin: "8px 0" }}>
        {candidates.map((c) => (
          <button key={c.id} className="result" aria-pressed={c.id === toId} data-link-candidate={c.id}
            onClick={() => setToId(c.id)}>
            <span>{c.title || deriveTitle(c.summary)}</span>
            <br /><span className="rk mono">{c.source || "—"}　{t("sheet.strings.head", {
              n: linkCountFor(props.links, c.id), cap: props.cap })}</span>
          </button>
        ))}
        {candidates.length === 0 && <p className="hint dim">{t("search.none")}</p>}
      </div>
      <div className="row">
        <select className="input" aria-label={t("link.type")} value={type}
          onChange={(e) => setType(e.target.value)} data-link-type>
          {["相關", "因果", "反例", "補充", "屬於"].map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <input className="input" aria-label={t("link.note")} placeholder={t("link.note")}
          value={note} onChange={(e) => setNote(e.target.value)} data-link-note />
      </div>
      {check && !check.allowed && <p className="hint bad" data-link-error>{codeText(check.code)}</p>}
      {check?.requiresReason && <p className="hint bad" data-link-error>{codeText(check.code)}</p>}
      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn primary" disabled={!ok} data-link-create
          onClick={() => props.onCreate(toId, type, note.trim())}>{t("link.create")}</button>
        <button className="btn" onClick={props.onCancel}>{t("edit.cancel")}</button>
      </div>
    </div>
  );
}
